import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Undo2, X, Star, Heart, Send, Crown, Loader2, Settings as Cog, Globe, Rocket } from "lucide-react";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Swipe (Tinder/Yubo-style) card deck with drag gestures.
 *
 * - Non-VIP: 10 swipes / 24h. VIP: unlimited + free DM + super-like.
 * - Drag the card left/right past the SWIPE_THRESHOLD to trigger nope/like.
 *   Below the threshold the card snaps back. Rotation + LIKE/NOPE badges
 *   provide the visual feedback used by Tinder / Bumble / Yubo.
 * - Tap-buttons remain as a fallback for accessibility and desktop use.
 */
const SWIPE_THRESHOLD = 120; // px past-centre before we commit the swipe

export default function Swipe() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [matchModal, setMatchModal] = useState(null);
  const [dmModal, setDmModal] = useState(null);
  const [decideBusy, setDecideBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/swipe/status");
      setStatus(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadDeck = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/swipe/deck?limit=10");
      setCards(data.cards || []);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); loadDeck(); }, [loadStatus, loadDeck]);

  const top = cards[0];

  const decide = async (direction, sourceCardId) => {
    if (!top || decideBusy) return;
    const targetId = sourceCardId || top.id;
    setDecideBusy(true);
    try {
      const { data } = await api.post("/swipe/decide", { target_id: targetId, direction });
      setHistory((h) => [top, ...h].slice(0, 5));
      setCards((cs) => cs.slice(1));
      loadStatus();
      if (data.match) setMatchModal({ user: top });
      if (cards.length <= 2) loadDeck();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "daily_limit_reached") toast.error(t("swipe.dailyLimitReached"));
      else if (code === "vip_required") toast.error(t("swipe.vipRequired"));
      else toast.error(fmtError(detail) || e.message);
    } finally {
      setDecideBusy(false);
    }
  };

  const undo = async () => {
    if (decideBusy || history.length === 0) return;
    setDecideBusy(true);
    try {
      const { data } = await api.post("/swipe/undo");
      if (data.restored) {
        // Put the restored profile back on top of the deck.
        setCards((cs) => [data.restored, ...cs]);
        setHistory((h) => h.slice(1));
        loadStatus();
        toast.success(t("swipe.undoSuccess", { name: data.restored.name }));
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "vip_required") toast.error(t("swipe.undoVipRequired"));
      else toast.error(fmtError(detail) || e.message);
    } finally { setDecideBusy(false); }
  };

  const openDm = () => {
    if (!top) return;
    if (status?.is_vip) {
      api.post("/swipe/dm-unlock", { target_id: top.id })
        .then(() => nav(`/chats?peer=${top.id}`))
        .catch((e) => toast.error(fmtError(e.response?.data?.detail) || e.message));
    } else {
      setDmModal({ user: top });
    }
  };

  const isEmpty = !loading && cards.length === 0;

  return (
    // h-screen fallback + 100dvh override: dvh tracks the *visible* viewport on
    // mobile (excludes browser chrome), so the flex column below always fits —
    // card shrinks, action buttons stay visible above the bottom nav.
    <div
      className="h-screen flex flex-col overflow-hidden bg-gradient-to-b from-white to-[#F4F1E8] text-[#0A0B0D] max-w-2xl mx-auto w-full"
      style={{ height: "100dvh" }}
      data-testid="swipe-page"
    >
      {/* Header */}
      <div className="shrink-0 z-30 bg-white/90 backdrop-blur border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-4">
          <h1 className="font-display font-black text-[26px] tracking-tight flex-1">{t("swipe.title")}</h1>
          <button onClick={() => nav("/matches")} className="w-10 h-10 rounded-full hover:bg-[#F4F1E8] flex items-center justify-center" aria-label="Matches" data-testid="swipe-matches-btn">
            <Heart className="w-5 h-5 text-[#00CED1]" strokeWidth={2.5} fill="#00CED1" />
          </button>
          <button onClick={() => nav("/settings/people-preferences")} className="w-10 h-10 rounded-full hover:bg-[#F4F1E8] flex items-center justify-center" aria-label="Preferences" data-testid="swipe-prefs-btn">
            <Cog className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
        {/* Quota strip */}
        {status && !status.is_vip && (
          <div className="px-5 pb-3 text-xs text-[#7C828D] font-semibold" data-testid="swipe-quota">
            {t("swipe.dailyLeft", { left: status.swipes_left, total: status.daily_limit })}
            {" · "}
            <button onClick={() => nav("/vip")} className="text-[#00CED1] hover:underline">{t("swipe.upgradeVip")}</button>
          </div>
        )}
        {status?.is_vip && (
          <div className="px-5 pb-3 flex items-center gap-2 flex-wrap">
            <div className="text-xs text-[#00CED1] font-bold uppercase tracking-wide flex items-center gap-1">
              <Crown className="w-3.5 h-3.5" strokeWidth={3} /> {t("swipe.vipUnlimited")}
            </div>
            <button
              onClick={() => nav("/settings/people-preferences")}
              data-testid="swipe-passport-badge"
              className={`text-xs font-bold uppercase tracking-wide flex items-center gap-1 px-2 py-1 rounded-full border-2 border-[#121212] transition-colors ${
                status.passport_location ? "bg-[#B49CFF] text-white" : "bg-[#F4F1E8] text-[#3D424B] hover:bg-[#E9E5D7]"
              }`}
            >
              <Globe className="w-3.5 h-3.5" strokeWidth={3} />
              {status.passport_location
                ? t("swipe.passportActive", { loc: status.passport_location })
                : t("swipe.passportInactive")}
            </button>
            <BoostButton status={status} refresh={loadStatus} refreshUser={refresh} t={t} nav={nav} />
          </div>
        )}
      </div>

      {/* Card area — flex-1 + min-h-0 lets the card shrink to whatever space
          is left after header, action buttons and bottom nav. No fixed vh math. */}
      <div className="flex-1 min-h-0 px-4 pt-3 pb-1 flex items-stretch justify-center touch-none select-none">
        {loading ? (
          <div className="flex items-center"><Loader2 className="w-8 h-8 animate-spin text-[#7C828D]" /></div>
        ) : isEmpty ? (
          <div className="flex items-center"><EmptyDeck t={t} onReload={loadDeck} /></div>
        ) : (
          <div className="relative w-full max-w-md h-full">
            {/* Next card peek (behind) */}
            {cards[1] && <ProfileCard u={cards[1]} peek />}
            {/* Top card (draggable) */}
            <AnimatePresence>
              {top && (
                <DraggableCard
                  key={top.id}
                  u={top}
                  disabled={decideBusy}
                  onSwipe={(dir) => decide(dir === "right" ? "like" : "nope", top.id)}
                />
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Action buttons — Tinder-style: solid dark circles, colored icons.
          pb clears the fixed bottom nav (bottom-4 + ~56px pill). */}
      {top && (
        <div className="shrink-0 px-4 sm:px-5 pt-2 pb-[96px] sm:pb-[104px] flex items-center justify-center gap-2 sm:gap-3" data-testid="swipe-actions">
          <ActionBtn onClick={undo} disabled={history.length === 0 || decideBusy} label={t("swipe.undo")} testid="swipe-undo">
            <Undo2 className="w-5 h-5 text-[#F5B547]" strokeWidth={2.75} />
          </ActionBtn>
          <ActionBtn onClick={() => decide("nope")} disabled={decideBusy} label={t("swipe.nope")} testid="swipe-nope" big>
            <X className="w-8 h-8 text-white" strokeWidth={3.5} />
          </ActionBtn>
          <ActionBtn onClick={() => decide("super")} disabled={decideBusy || !status?.is_vip} label={t("swipe.super")} testid="swipe-super">
            <Star
              className={`w-5 h-5 ${status?.is_vip ? "text-[#5FBAF7]" : "text-[#5FBAF7]/50"}`}
              strokeWidth={2.5}
              fill={status?.is_vip ? "#5FBAF7" : "none"}
            />
          </ActionBtn>
          <ActionBtn onClick={() => decide("like")} disabled={decideBusy} label={t("swipe.like")} testid="swipe-like" big>
            <Heart className="w-8 h-8 text-[#FF4757]" strokeWidth={2.5} fill="#FF4757" />
          </ActionBtn>
          <ActionBtn onClick={openDm} disabled={decideBusy} label={t("swipe.dm")} testid="swipe-dm">
            <Send className="w-5 h-5 text-[#5FBAF7]" strokeWidth={2.5} fill="#5FBAF7" />
          </ActionBtn>
        </div>
      )}

      {/* Modals */}
      {matchModal && <MatchModal me={user} user={matchModal.user} onClose={() => setMatchModal(null)} onChat={() => nav(`/chats?peer=${matchModal.user.id}`)} t={t} />}
      {dmModal && (
        <DmUnlockModal
          target={dmModal.user}
          coins={user?.coins ?? 0}
          cost={status?.dm_unlock_cost ?? 50}
          onClose={() => setDmModal(null)}
          onPaid={async () => { setDmModal(null); await refresh(); nav(`/chats?peer=${dmModal.user.id}`); }}
          t={t}
        />
      )}
    </div>
  );
}

function DraggableCard({ u, disabled, onSwipe }) {
  const x = useMotionValue(0);
  // Rotation: -18°..18° across the drag range — Tinder-esque tilt.
  const rotate = useTransform(x, [-300, 0, 300], [-18, 0, 18]);
  const likeOpacity = useTransform(x, [40, SWIPE_THRESHOLD], [0, 1]);
  const nopeOpacity = useTransform(x, [-SWIPE_THRESHOLD, -40], [1, 0]);

  const handleDragEnd = (_e, info) => {
    if (disabled) return;
    const { offset, velocity } = info;
    const swipePower = Math.abs(offset.x) * (Math.abs(velocity.x) > 500 ? 1.5 : 1);
    if (swipePower > SWIPE_THRESHOLD) {
      onSwipe(offset.x > 0 ? "right" : "left");
    }
  };

  return (
    <motion.div
      data-testid="swipe-card"
      className="absolute inset-0 cursor-grab active:cursor-grabbing"
      style={{ x, rotate }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      whileTap={{ scale: 0.98 }}
      initial={{ scale: 1, opacity: 1 }}
      exit={{
        x: x.get() > 0 ? 600 : -600,
        opacity: 0,
        transition: { duration: 0.28, ease: "easeOut" },
      }}
    >
      <div className="relative w-full h-full rounded-3xl overflow-hidden bg-[#F4F1E8] border-2 border-[#121212] shadow-[6px_6px_0_#121212]">
        <CardMedia u={u} />
        <CardOverlay u={u} />
        {/* LIKE / NOPE badges */}
        <motion.div
          style={{ opacity: likeOpacity }}
          className="absolute top-8 left-6 border-[6px] border-[#00CE9E] text-[#00CE9E] font-black text-4xl px-4 py-1 rounded-xl rotate-[-18deg] pointer-events-none tracking-wider"
          data-testid="swipe-badge-like"
        >
          LIKE
        </motion.div>
        <motion.div
          style={{ opacity: nopeOpacity }}
          className="absolute top-8 right-6 border-[6px] border-[#FF4E5B] text-[#FF4E5B] font-black text-4xl px-4 py-1 rounded-xl rotate-[18deg] pointer-events-none tracking-wider"
          data-testid="swipe-badge-nope"
        >
          NOPE
        </motion.div>
      </div>
    </motion.div>
  );
}

function ProfileCard({ u, peek }) {
  return (
    <div
      className={`absolute inset-0 rounded-3xl overflow-hidden bg-[#F4F1E8] border-2 border-[#121212] shadow-[6px_6px_0_#121212] ${peek ? "scale-[0.96] translate-y-3 opacity-70" : ""}`}
      data-testid={peek ? "swipe-card-peek" : "swipe-card-static"}
    >
      <CardMedia u={u} />
      <CardOverlay u={u} />
    </div>
  );
}

function CardMedia({ u }) {
  const photos = (u.photos && u.photos.length > 0) ? u.photos : (u.avatar_url ? [u.avatar_url] : []);
  return photos[0] ? (
    <img src={photos[0]} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
  ) : (
    <div className="w-full h-full flex items-center justify-center font-display text-8xl font-black text-[#7C828D]">
      {(u.name || "?")[0]?.toUpperCase()}
    </div>
  );
}

function CardOverlay({ u }) {
  return (
    <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white pointer-events-none">
      <div className="flex items-center gap-2 mb-1">
        <h2 className="font-display font-black text-3xl tracking-tight">{u.name}, {u.age}</h2>
        {u.verified && <span className="bg-[#00CED1] text-black text-[10px] font-black px-2 py-0.5 rounded-full">✓</span>}
      </div>
      {u.location && <p className="text-white/80 text-sm mb-2">{u.location}</p>}
      {u.bio && <p className="text-white/90 text-sm line-clamp-2">{u.bio}</p>}
      {u.interests?.length > 0 && (
        <div className="flex gap-1.5 flex-wrap mt-2">
          {u.interests.slice(0, 4).map((tag) => (
            <span key={tag} className="bg-white/20 backdrop-blur text-white text-[10px] font-black px-2 py-1 rounded uppercase tracking-wide">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ children, onClick, disabled, label, testid, big }) {
  // Tinder-style: solid dark circle, no border, gentle drop shadow. Two sizes
  // — Like/Nope are larger (primary actions), Undo/Super/DM stay smaller.
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testid}
      className={`${big ? "w-14 h-14 sm:w-16 sm:h-16" : "w-11 h-11 sm:w-12 sm:h-12"} rounded-full flex items-center justify-center bg-[#1F1F1F] shadow-[0_6px_16px_rgba(0,0,0,0.18)] transition-all active:scale-90 hover:brightness-125 disabled:opacity-35 disabled:cursor-not-allowed disabled:active:scale-100`}
    >
      {children}
    </button>
  );
}

function EmptyDeck({ t, onReload }) {
  return (
    <div className="text-center py-16 max-w-sm mx-auto" data-testid="swipe-empty">
      <div className="w-20 h-20 rounded-full bg-[#F4F1E8] border-2 border-[#121212] mx-auto mb-4 flex items-center justify-center">
        <Heart className="w-9 h-9 text-[#7C828D]" strokeWidth={2.5} />
      </div>
      <h3 className="font-display font-black text-xl mb-1">{t("swipe.emptyTitle")}</h3>
      <p className="text-sm text-[#7C828D] mb-4">{t("swipe.emptyBody")}</p>
      <button onClick={onReload} className="bg-[#00CED1] text-black font-black py-2.5 px-5 rounded-full border-2 border-[#121212] shadow-[3px_3px_0_#121212] active:translate-y-[1px]">{t("swipe.reload")}</button>
    </div>
  );
}

function MatchAvatar({ url, initial, ring, rotate, fromX }) {
  return (
    <motion.div
      initial={{ x: fromX, opacity: 0, rotate: 0 }}
      animate={{ x: 0, opacity: 1, rotate }}
      transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.15 }}
      className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 shadow-[0_10px_30px_rgba(0,0,0,0.45)] bg-[#2A2A2A] flex items-center justify-center"
      style={{ borderColor: ring }}
    >
      {url ? (
        <img src={url} alt="" className="w-full h-full object-cover" draggable={false} />
      ) : (
        <span className="font-display font-black text-4xl text-white/70">{initial}</span>
      )}
    </motion.div>
  );
}

function MatchModal({ me, user, onClose, onChat, t }) {
  const myPhoto = me?.photos?.[0] || me?.avatar_url || "";
  const theirPhoto = user.photos?.[0] || user.avatar_url || "";
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[90] bg-[#0B0B0F]/95 backdrop-blur-sm flex flex-col items-center justify-center px-8 text-center"
      data-testid="match-modal"
    >
      {/* Title — Tinder-style gradient script */}
      <motion.h2
        initial={{ scale: 0.6, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 16 }}
        className="font-display font-black italic text-5xl sm:text-6xl tracking-tight bg-gradient-to-r from-[#00CED1] via-[#5FBAF7] to-[#B49CFF] bg-clip-text text-transparent mb-2"
      >
        {t("swipe.itsMatch")}
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-white/70 text-sm mb-10"
      >
        {t("swipe.matchWith", { name: user.name })}
      </motion.p>

      {/* Overlapping avatars */}
      <div className="flex items-center justify-center mb-12 -space-x-5">
        <MatchAvatar url={myPhoto} initial={(me?.name || "?")[0]?.toUpperCase()} ring="#00CED1" rotate={-8} fromX={-120} />
        <MatchAvatar url={theirPhoto} initial={(user.name || "?")[0]?.toUpperCase()} ring="#FF4757" rotate={8} fromX={120} />
      </div>

      {/* Actions — match the dark circular swipe-button aesthetic */}
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.45 }}
        className="w-full max-w-xs space-y-3"
      >
        <button
          onClick={onChat}
          className="w-full py-3.5 rounded-full font-black text-[#0B0B0F] bg-gradient-to-r from-[#00CED1] to-[#5FBAF7] shadow-[0_8px_24px_rgba(0,206,209,0.35)] transition-transform active:scale-95 flex items-center justify-center gap-2"
          data-testid="match-chat"
        >
          <Send className="w-4 h-4" strokeWidth={3} /> {t("swipe.sendMessage")}
        </button>
        <button
          onClick={onClose}
          className="w-full py-3.5 rounded-full font-bold text-white/85 border border-white/25 hover:bg-white/10 transition-colors active:scale-95"
          data-testid="match-close"
        >
          {t("swipe.keepSwiping")}
        </button>
      </motion.div>
    </motion.div>
  );
}

function DmUnlockModal({ target, coins, cost, onClose, onPaid, t }) {
  const [busy, setBusy] = useState(false);
  const canAfford = coins >= cost;

  const pay = async () => {
    setBusy(true);
    try {
      await api.post("/swipe/dm-unlock", { target_id: target.id });
      toast.success(t("swipe.dmUnlocked"));
      onPaid();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center" data-testid="dm-unlock-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 border-t-4 sm:border-4 border-[#B49CFF] shadow-2xl">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-[#B49CFF] flex items-center justify-center shrink-0">
            <Send className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="font-display font-black text-xl">{t("swipe.dmUnlockTitle")}</h2>
            <p className="text-sm text-[#3D424B]">{t("swipe.dmUnlockBody", { name: target.name, cost })}</p>
          </div>
        </div>

        <div className="rounded-2xl bg-[#F4F1E8] p-3 mb-4 flex items-center justify-between text-sm">
          <span className="text-[#7C828D] font-bold uppercase tracking-wide text-xs">{t("swipe.yourBalance")}</span>
          <span className="font-black text-lg">🪙 {coins}</span>
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 bg-[#F4F1E8] text-black font-bold py-3 rounded-2xl" data-testid="dm-cancel">{t("common.cancel")}</button>
          <button
            onClick={pay}
            disabled={busy || !canAfford}
            className="flex-1 bg-[#B49CFF] text-white font-black py-3 rounded-2xl border-2 border-[#121212] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="dm-pay"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={3} />}
            {canAfford ? t("swipe.dmPay", { cost }) : t("swipe.dmNotEnough")}
          </button>
        </div>
        {!canAfford && (
          <button onClick={() => (window.location.href = "/coins")} className="mt-2 w-full text-[#00CED1] font-bold text-sm underline">{t("swipe.buyMoreCoins")}</button>
        )}
      </div>
    </div>
  );
}


function BoostButton({ status, refresh, refreshUser, t, nav }) {
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState("");

  // Tick a "Xh Ym left" label every 60s while a boost is active.
  useEffect(() => {
    if (!status?.boost_until) { setRemaining(""); return; }
    const tick = () => {
      const end = new Date(status.boost_until).getTime();
      const diffMin = Math.max(0, Math.round((end - Date.now()) / 60000));
      if (diffMin <= 0) { setRemaining(""); return; }
      const h = Math.floor(diffMin / 60);
      const m = diffMin % 60;
      setRemaining(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [status?.boost_until]);

  const activate = async () => {
    if (busy) return;
    if (!window.confirm(t("swipe.boostConfirm", { cost: status?.boost_cost ?? 200 }))) return;
    setBusy(true);
    try {
      await api.post("/swipe/boost");
      toast.success(t("swipe.boostActivated"));
      await refresh();
      await refreshUser();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "not_enough_coins") {
        toast.error(t("swipe.boostNoCoins"));
        setTimeout(() => nav("/coins"), 1200);
      } else if (code === "vip_required") {
        toast.error(t("swipe.boostVipRequired"));
      } else {
        toast.error(fmtError(detail) || e.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <button
      onClick={activate}
      disabled={busy}
      data-testid="swipe-boost-btn"
      className={`text-xs font-bold uppercase tracking-wide flex items-center gap-1 px-2 py-1 rounded-full border-2 border-[#121212] transition-all disabled:opacity-60 ${
        status?.boost_active
          ? "bg-gradient-to-r from-[#F9C349] to-[#FFB347] text-[#4A3000]"
          : "bg-[#F4F1E8] text-[#3D424B] hover:bg-[#E9E5D7]"
      }`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" strokeWidth={3} />}
      {status?.boost_active && remaining
        ? t("swipe.boostActive", { remaining })
        : t("swipe.boost", { cost: status?.boost_cost ?? 200 })}
    </button>
  );
}
